/**
 * MS-REC-082 — detect stale journey checkpoint after refresh mid payment-pending flow.
 */
import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { buildRecoveryPath } from '../../shared/recovery/useRecoveryNavigation.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import {
  clearJourneyCheckpoint,
  consumeDocumentReloadSignal,
  consumeJourneyReloadFlag,
  readJourneyCheckpoint,
} from './journeyCheckpoint.js';

/**
 * Playwright hermetic harness may set `window.__RPAPP_E2E_DISABLE_INTERRUPTED_RECOVERY__`
 * so account-route matrix navigations to payment-pending surfaces are not bounced to
 * `/recovery/interrupted` (Vite/Playwright document churn can arm reload falsely).
 * Honored only when NODE_ENV !== production (ToastHost / TenantProvider pattern).
 */
function isE2eInterruptedRecoveryDisabled(): boolean {
  if (process.env.NODE_ENV === 'production') {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  return window.__RPAPP_E2E_DISABLE_INTERRUPTED_RECOVERY__ === true;
}

export function useInterruptedJourneyRecovery(): void {
  const tenantCode = useTenantCode();
  const location = useLocation();

  useLayoutEffect(() => {
    if (isE2eInterruptedRecoveryDisabled()) {
      return;
    }
    // Consume Navigation Timing once per document ASAP — type=reload is sticky for the
    // whole SPA session after F5. Deferring consume until interrupted=true would let a
    // later SPA pagehide falsely open /recovery/interrupted (MOB-05 / G7).
    const navReload = consumeDocumentReloadSignal();

    if (location.pathname.includes('/recovery/')) {
      return;
    }

    const checkpoint = readJourneyCheckpoint(tenantCode);
    if (checkpoint === null || checkpoint.interrupted !== true) {
      return;
    }

    // MS-REC-082: recover only after a real document reload.
    // - beforeunload → one-shot session reload flag (desktop / Android; iOS often skips)
    // - Navigation Timing type=reload → one-shot per document (iPhone PWA path)
    // Never treat SPA history POP `pagehide` as reload — pagehide must not arm the flag.
    const reloaded = consumeJourneyReloadFlag(tenantCode) || navReload;
    if (!reloaded) {
      return;
    }

    const recoveryPath = buildRecoveryPath(tenantCode, 'interrupted', {
      returnTo: checkpoint.returnTo,
      paymentId: checkpoint.paymentId,
      journey: checkpoint.journey,
    });
    clearJourneyCheckpoint(tenantCode);
    // Hard replace (not React navigate): checkout remount races navigate on reload and
    // can re-write the pending checkpoint before the recovery route commits.
    window.location.replace(recoveryPath);
  }, [location.pathname, tenantCode]);
}
